import { api } from '../../lib/api'
import { useResource, Tile, Card, PageHeader, LoadingState } from '@retink/web-ui'

interface TenantSettings {
  id: string; name: string; slug: string; region: string | null; status: string
  org_name: string; org_slug: string; plan_code: string | null; plan_name: string | null
}
interface Member { id: string; tenant_role: string; cc_scope: string | null; membership_status: string }
interface Invite { id: string }

// The tenant admin's home overview — the tenant identity card plus live team counts.
// Mirrors the account/admin panel of the workspace dashboard, but reads the console's
// own /api/tenant-admin/* endpoints (own tenant, from the JWT claim).
export function TenantOverview() {
  const { data } = useResource<{
    tenant: TenantSettings
    members: Member[]
    invites: Invite[]
  }>(() =>
    Promise.all([
      api.get<TenantSettings>('/api/tenant-admin/tenant'),
      api.get<{ members: Member[]; pendingInvites: Invite[] }>('/api/tenant-admin/members'),
    ]).then(([tenant, m]) => ({ tenant, members: m.members, invites: m.pendingInvites })), [])

  if (!data) return <div className="p-8"><LoadingState /></div>

  const { tenant, members, invites } = data
  const activeMembers = members.filter((m) => m.membership_status === 'active').length
  const adminCount = members.filter((m) => m.tenant_role === 'admin').length
  const ccCount = members.filter((m) => m.cc_scope).length

  return (
    <div className="p-8">
      <PageHeader
        title={tenant.name}
        description={
          <span className="font-mono">
            {tenant.slug} · {tenant.org_name} · {tenant.region ?? '—'}
            {tenant.plan_name && <> · {tenant.plan_name}</>}
          </span>
        }
      />

      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Tile label="Members" value={activeMembers} to="/tenant-home/team" />
        <Tile label="Tenant admins" value={adminCount} to="/tenant-home/team" />
        <Tile label="Call-centre" value={ccCount} />
        <Tile label="Pending invites" value={invites.length} to={invites.length ? '/tenant-home/team' : undefined} />
      </div>

      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Tenant</h2>
        <dl className="grid grid-cols-[7rem_1fr] gap-y-1 text-sm">
          <dt className="text-muted-foreground">Organization</dt><dd>{tenant.org_name}</dd>
          <dt className="text-muted-foreground">Region</dt><dd>{tenant.region ?? '—'}</dd>
          <dt className="text-muted-foreground">Plan</dt><dd>{tenant.plan_name ?? 'Default'}</dd>
          <dt className="text-muted-foreground">Status</dt><dd>{tenant.status}</dd>
        </dl>
      </Card>
    </div>
  )
}
